import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import { Button } from "@shapr3d/frontend-lib";
import { useDropzone } from "react-dropzone";

import useTranslation from "../../hooks/useTranslation";

const ModelViewer = React.lazy(() => import("../../components/ModelViewer/ModelViewer"));

import style from "./ModelViewerDebugPage.module.css";

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

export default function ModelViewerDebugPage() {
  const { t, m } = useTranslation();
  const [modelURL, setModelURL] = useState<string | undefined>();
  const modelViewerRef = useRef<ModelViewerElement>(null);

  useEffect(() => {
    const getLocalModel = async () => {
      const response = await fetch("/local.glb");
      if (response.status === 200 && response.headers.get("Content-Type") === "model/gltf-binary") {
        const blob = await response.blob();
        setModelURL(URL.createObjectURL(blob));
      }
    };
    getLocalModel();
  }, []);

  useEffect(() => {
    if (modelURL) {
      return () => URL.revokeObjectURL(modelURL);
    }
  }, [modelURL]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      setModelURL(URL.createObjectURL(acceptedFiles[0]));
    }
  }, []);
  const { isDragActive, getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        "model/gltf-binary": [".glb"],
        "model/gltf+json": [".gltf"],
      },
    });
  const dropZoneStlye = useMemo(
    () => ({
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  if (!modelURL) {
    return (
      <div className={style.dropZoneContainer}>
        <div className={style.dropZone} {...getRootProps({ style: dropZoneStlye })}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>{t(m.ModelViewerDebug.DropFileHere)}</p>
          ) : (
            <p>{t(m.ModelViewerDebug.DropOrClick)}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.buttonContainer}>
        <Button
          variant="grayscale"
          onClick={() => {
            setModelURL(undefined);
          }}
        >
          {t(m.ModelViewerDebug.UploadNew)}
        </Button>
        <Button
          variant="grayscale"
          onClick={() => {
            modelViewerRef?.current?.activateAR?.();
          }}
        >
          {t(m.ModelViewer.ArButtonLabel)}
        </Button>
      </div>
      <Suspense>
        <ModelViewer ref={modelViewerRef} src={modelURL} />
      </Suspense>
    </div>
  );
}
